import { AutoComplete, AutoCompleteProps } from "antd";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AirplaneIcon } from "@Icons/index";
import { FloatingLabelOptions, withFloatingLabel } from "src/HOC/WithFloatingLabel";
import { mapOption } from "../utils";
import PulseLoadingIndicator from "@Components/PulseLoadingIndicator/PulseLoadingIndicator";
import { useToast } from "src/context";

const FloatingFlightSelectWithLabel = withFloatingLabel(AutoComplete);

type FlightSelectWebProps = FloatingLabelOptions &
  AutoCompleteProps & {
    options?: ReturnType<typeof mapOption>[];
    isFetching?: boolean;
    searchValue?: string;
    onSearch?: (value: string) => void;
    error: any;
    validationError: any;
  };

const FlightSelectNewWeb: FC<FlightSelectWebProps> = ({
  onChange,
  options = [],
  isFetching,
  searchValue,
  error,
  onSearch,
  value: controlledValue,
  ...props
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  useEffect(() => {
    if (isFetching || !error) return;
    toast.error(t("Flights.search.noAirportsFound", { query: searchValue }));
  }, [error, isFetching]);

  const text = searchValue ?? (controlledValue as any)?.label ?? "";
  const selectOptions = options.map((option) => ({ ...option, value: option.label }));

  return (
    <FloatingFlightSelectWithLabel
      value={text}
      float={Boolean(text)}
      blurOnChange={false}
      variant="borderless"
      className="border-none! outline-none! shadow-none!"
      showSearch={{
        onSearch,
        filterOption:true,
        searchValue,
      }}
      suffix={<PulseLoadingIndicator show={isFetching} running={isFetching} className={"bg-background-primary-lighter!"} />}
      notFoundContent={null}
      popupRender={(menu) => {
        return (
          <div className="min-w-70! md:min-w-100! w-full! [&_.rc-virtual-list]:w-full! [&_.rc-virtual-list-holder]:scrollbar-hide! flex items-center justify-center">
            {menu}
          </div>
        );
      }}
      popupMatchSelectWidth={false}
      options={selectOptions}
      onSelect={(_, option: any) => {
        onChange?.({ value: option.iataCode, label: option.label });
      }}
      classNames={{
        popup: {
          list: "w-full!",
          root: "w-full!",
          listItem: "w-full!",
        },
        root: "w-full! border-none! shadow-none!",
        label: "text-font-minimal!",
      }}
      optionRender={(option) => {
        const subLabel = option?.data?.city !== "" ? `${option.data.city}, ` : "";
        return (
          <div className="w-full! hover:text-font-active flex items-center gap-1 p-1 px-5">
            <div className="flex h-1 w-1 flex-1 items-center justify-center font-bold">
              <AirplaneIcon strokeWidth={1.2} transform="rotate(-45 0 0) scale(.8,.8)" />
            </div>
            <div className="flex-9 text-font-default hover:font-default flex flex-col items-start justify-center">
              <span className="whitespace-break-spaces font-semibold">
                {option.data.airport} ({option.data.iataCode})
              </span>
              <span className="text-font-subtle">
                {subLabel} {option.data.country}
              </span>
            </div>
          </div>
        );
      }}
      {...props}
    />
  );
};

export default FlightSelectNewWeb;
